import { isZyVersionNodeVisible, normalizeSelectedZyVersionFilter, type SelectedZyVersionFilter } from '@/lib/zy-tiptap-utils';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';

/**
 * ProseMirror plugin that keeps the selection out of hidden zyVersion nodes.
 * When a transaction would place the caret inside a hidden zyVersion (block or inline) node,
 * the plugin adjusts the selection to the nearest visible position in the direction the user
 * was moving (e.g. arrow key), so the caret never "lands" in hidden version-specific content.
 * Uses getVersionFilter (e.g. from extension storage or doc attr) for the active version filter.
 */
export function createZyVersionSelectionPlugin(getVersionFilter: () => SelectedZyVersionFilter): Plugin {
    return new Plugin({
        key: new PluginKey('zyVersionSelection'),

        filterTransaction(tr, state) {
            const selectedZyVersion = getVersionFilter() ?? null;
            if (normalizeSelectedZyVersionFilter(selectedZyVersion) === null) return true;

            const nextFrom = tr.selection.from;
            const range = findHiddenZyVersionRange(tr.doc, nextFrom, selectedZyVersion);
            if (!range) return true;

            const prevPos = state.selection.from;
            const direction: 1 | -1 = nextFrom > prevPos ? 1 : -1;
            const target = findNextVisiblePos(tr.doc, nextFrom, direction, selectedZyVersion);
            if (target === nextFrom) return true;

            const resolved = tr.doc.resolve(target);
            tr.setSelection(TextSelection.near(resolved));
            return true;
        },
    });
}

/**
 * Finds the next document position that is not inside a hidden zyVersion node, moving in the given direction.
 * Used by the selection plugin to redirect the caret when it would land inside hidden version-specific content.
 *
 * @param doc - The ProseMirror document.
 * @param from - Starting position to search from.
 * @param direction - 1 to search forward, -1 to search backward.
 * @returns A position outside any hidden zyVersion range, or doc boundary (doc.content.size or 0) if none found.
 */
function findNextVisiblePos(doc: PMNode, from: number, direction: 1 | -1, selectedZyVersion: SelectedZyVersionFilter): number {
    if (normalizeSelectedZyVersionFilter(selectedZyVersion) === null) return from;
    const len = doc.content.size;
    if (direction > 0) {
        let pos = Math.min(from + 1, len);
        while (pos <= len) {
            const range = findHiddenZyVersionRange(doc, pos, selectedZyVersion);
            if (!range) return pos;
            pos = range.to + 1;
        }
        return len;
    } else {
        let pos = Math.max(from - 1, 0);
        while (pos >= 0) {
            const range = findHiddenZyVersionRange(doc, pos, selectedZyVersion);
            if (!range) return pos;
            pos = range.from - 1;
        }
        return 0;
    }
}

/**
 * If the given position lies inside a hidden zyVersion (block or inline) node, returns that node's range.
 * Walks up from the position to find an enclosing zyVersionBlock or zyVersionInline that is hidden
 * for the current selectedZyVersion.
 *
 * @param doc - The ProseMirror document.
 * @param pos - A document position.
 * @param selectedZyVersion - The active version filter (`null` shows all, `[]` shows none).
 * @returns The { from, to } range of the hidden zyVersion node containing pos, or null if pos is not inside one.
 */
function findHiddenZyVersionRange(
    doc: PMNode,
    pos: number,
    selectedZyVersion: SelectedZyVersionFilter,
): { from: number; to: number } | null {
    if (normalizeSelectedZyVersionFilter(selectedZyVersion) === null) return null;
    const $pos = doc.resolve(pos);
    for (let d = $pos.depth; d > 0; d--) {
        const node = $pos.node(d);
        if (node.type.name === 'zyVersionBlock' || node.type.name === 'zyVersionInline') {
            const version = node.attrs.version ?? '';
            if (!isZyVersionNodeVisible(selectedZyVersion, version)) {
                const from = $pos.before(d);
                const to = $pos.after(d);
                return { from, to };
            }
            return null;
        }
    }
    return null;
}
